import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useDashboard } from '../hooks/useDashboard';
import { useYearlyStats } from '../hooks/useYearlyStats';
import { useThreeWeekCalendar } from '../hooks/useThreeWeekCalendar';
import { useShowroom } from '../hooks/useShowroom';
import { useCanales } from '../hooks/useCanales';
import { useOdooBilling } from '../hooks/useOdooBilling';
import { fetchDatabases, sendSlackSummary } from '../utils/api';
import WeeklyCalendar from '../components/WeeklyCalendar';
import SummaryCards from '../components/SummaryCards';
import TenantStatsTable from '../components/TenantStatsTable';
import ThreeWeekCalendar from '../components/ThreeWeekCalendar';
import Showroom from '../components/Showroom';
import Canales from '../components/Canales';
import OdooBilling from '../components/OdooBilling';

/**
 * Main dashboard page.
 */
function Dashboard({ onLogout }) {
  const currentMonth = format(new Date(), 'yyyy-MM');
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [databases, setDatabases] = useState([]);
  const [selectedDb, setSelectedDb] = useState(1);
  const [view, setView] = useState('main');
  const [slackStatus, setSlackStatus] = useState('idle'); // idle | sending | sent | error
  const [slackError, setSlackError] = useState(null);

  useEffect(() => {
    fetchDatabases()
      .then(list => {
        setDatabases(list);
        if (list.length > 0) setSelectedDb(list[0].index);
      })
      .catch(() => {});
  }, []);

  const { weeklyAuctions, tenantStats, summary, loading, error, refetch } = useDashboard(selectedMonth, selectedDb);
  const {
    years: availableYears,
    tenantStats: yearlyTenantStats,
    summary: yearlySummary,
    loading: yearlyLoading,
    error: yearlyError,
    refetch: yearlyRefetch,
  } = useYearlyStats(selectedYear, selectedDb);
  const { auctions: threeWeekAuctions, loading: calLoading, error: calError, lastRefresh, refetch: calRefetch } = useThreeWeekCalendar(selectedDb);
  const { lots: showroomLots, loading: showroomLoading, error: showroomError, refetch: showroomRefetch } = useShowroom(selectedDb);
  const { tenants: canalesTenants, loading: canalesLoading, error: canalesError, refetch: canalesRefetch } = useCanales(selectedDb);
  const { entries: odooEntries, config: odooConfig, loading: odooLoading, error: odooError, refetch: odooRefetch } = useOdooBilling();

  const activeDb = databases.find(d => d.index === selectedDb);
  const refetchAnalytics = () => { refetch(); yearlyRefetch(); };
  const activeRefetch = view === 'calendar' ? calRefetch : view === 'showroom' ? showroomRefetch : view === 'canales' ? canalesRefetch : view === 'odoo' ? odooRefetch : refetchAnalytics;
  const activeError = view === 'calendar' ? calError : view === 'showroom' ? showroomError : view === 'canales' ? canalesError : view === 'odoo' ? odooError : (error || yearlyError);

  const handleSendSlack = async () => {
    setSlackStatus('sending');
    setSlackError(null);
    try {
      await sendSlackSummary(selectedDb);
      setSlackStatus('sent');
    } catch (err) {
      setSlackStatus('error');
      setSlackError(err.message);
    } finally {
      setTimeout(() => setSlackStatus('idle'), 3500);
    }
  };

  const slackButtonLabel = {
    idle: '📤 Enviar a Slack',
    sending: 'Enviando…',
    sent: '✓ Enviado',
    error: '✗ Error',
  }[slackStatus];

  const TABS = [
    { key: 'main',     label: 'Analítica' },
    { key: 'calendar', label: 'Calendario 3 semanas' },
    { key: 'showroom', label: 'Sala de exhibición' },
    { key: 'canales',  label: 'Canales' },
    { key: 'odoo',     label: 'Facturación Odoo' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FC', fontFamily: "'Rubik', sans-serif" }}>
      {/* Top nav */}
      <header style={{
        background: '#021344',
        padding: '0 32px',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 1px 0 rgba(255,255,255,0.07)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {/* Wordmark */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 7,
              background: '#1F50FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.95rem', fontWeight: 700, color: '#fff',
              boxShadow: '0 2px 8px rgba(31,80,255,0.40)',
            }}>R</div>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: '1rem', letterSpacing: '-0.01em' }}>
              Remate
            </span>
          </div>

          {/* View tabs */}
          <nav style={{ display: 'flex', gap: 2 }}>
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setView(tab.key)}
                style={{
                  background: view === tab.key ? '#1F50FF' : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  color: view === tab.key ? '#fff' : 'rgba(255,255,255,0.5)',
                  padding: '5px 14px',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  transition: 'background 0.15s, color 0.15s',
                  fontFamily: 'inherit',
                }}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* DB selector */}
          {databases.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                value={selectedDb}
                onChange={e => setSelectedDb(parseInt(e.target.value, 10))}
                style={{
                  background: '#0C2066',
                  border: '1px solid rgba(31,80,255,0.35)',
                  borderRadius: 6,
                  color: '#fff',
                  padding: '5px 10px',
                  fontSize: '0.8125rem',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              >
                {databases.map(db => (
                  <option key={db.index} value={db.index}>
                    {db.label}
                  </option>
                ))}
              </select>
              {activeDb && (
                <span style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 9999,
                  background: activeDb.isV3 ? 'rgba(255,153,0,0.15)' : 'rgba(31,80,255,0.15)',
                  color: activeDb.isV3 ? '#FF9900' : '#7E9BFF',
                  border: `1px solid ${activeDb.isV3 ? 'rgba(255,153,0,0.35)' : 'rgba(31,80,255,0.35)'}`,
                }}>
                  {activeDb.isV3 ? 'v3' : 'v1'}
                </span>
              )}
            </div>
          )}

          <button
            onClick={handleSendSlack}
            disabled={slackStatus === 'sending'}
            title={slackStatus === 'error' ? slackError : 'Enviar el resumen semanal a Slack ahora'}
            style={{
              background: slackStatus === 'sent' ? '#16A34A' : slackStatus === 'error' ? '#DC2626' : '#0C2066',
              border: '1px solid rgba(31,80,255,0.25)',
              borderRadius: 6,
              color: '#fff',
              padding: '5px 14px',
              cursor: slackStatus === 'sending' ? 'default' : 'pointer',
              fontSize: '0.8125rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
              opacity: slackStatus === 'sending' ? 0.7 : 1,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (slackStatus === 'idle') e.currentTarget.style.background = '#1F50FF'; }}
            onMouseLeave={e => { if (slackStatus === 'idle') e.currentTarget.style.background = '#0C2066'; }}
          >
            {slackButtonLabel}
          </button>

          <button
            onClick={activeRefetch}
            style={{
              background: '#0C2066',
              border: '1px solid rgba(31,80,255,0.25)',
              borderRadius: 6,
              color: 'rgba(255,255,255,0.7)',
              padding: '5px 14px',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1F50FF'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#0C2066'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
          >
            ↻ Actualizar
          </button>

          <button
            onClick={onLogout}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 6,
              color: 'rgba(255,255,255,0.5)',
              padding: '5px 14px',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: 500,
              fontFamily: 'inherit',
              transition: 'border-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
          >
            Cerrar sesión
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 24px' }}>

        {/* Error banner */}
        {activeError && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            padding: '12px 18px',
            color: '#DC2626',
            fontSize: '0.875rem',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ fontSize: '1rem' }}>⚠</span>
            <span>No se pudo conectar con la API: <strong>{activeError}</strong>. Asegúrate de que el backend esté activo.</span>
          </div>
        )}

        {view === 'calendar' ? (
          /* ── Vista Calendario 3 Semanas ── */
          <section>
            <SectionHeader
              title="Calendario de Remates — 3 Semanas"
              subtitle={`Todos los clientes · Semana pasada, actual y próxima${lastRefresh ? ` · Actualizado ${format(lastRefresh, 'HH:mm')} · se actualiza cada 30 min` : ''}`}
              loading={calLoading}
            />
            {calLoading ? <ThreeWeekSkeleton /> : <ThreeWeekCalendar auctions={threeWeekAuctions} />}
          </section>

        ) : view === 'showroom' ? (
          /* ── Vista Sala de Exhibición ── */
          <section>
            <SectionHeader
              title="Sala de Exhibición"
              subtitle="Lotes de remates en postulaciones, cerrados o en transmisión · Haz clic para ver el remate"
              loading={showroomLoading}
            />
            {showroomLoading ? <ThreeWeekSkeleton /> : <Showroom lots={showroomLots} />}
          </section>

        ) : view === 'canales' ? (
          /* ── Vista Canales ── */
          <section>
            <SectionHeader
              title="Canales"
              subtitle="Canales de transmisión por cliente"
              loading={canalesLoading}
            />
            {canalesLoading ? <TableSkeleton /> : <Canales tenants={canalesTenants} />}
          </section>

        ) : view === 'odoo' ? (
          /* ── Vista Facturación Odoo ── */
          <section>
            <SectionHeader
              title="Facturación Odoo — P&J y Prelco"
              subtitle="Remates finalizados del mes enviados al webhook de Odoo (P&J S01843 · Prelco S01806)"
              loading={odooLoading}
            />
            {odooLoading && !odooConfig
              ? <TableSkeleton />
              : <OdooBilling entries={odooEntries} config={odooConfig} onRefresh={odooRefetch} />}
          </section>

        ) : (
          /* ── Vista Analítica ── */
          <>
            {/* ── Sección 1: Calendario Semanal ── */}
            <section style={{ marginBottom: 40 }}>
              <SectionHeader
                title="Remates de esta semana"
                subtitle={format(new Date(), "'Semana del' d 'de' MMMM 'de' yyyy", { locale: es })}
                loading={loading}
              />
              {loading ? <CalendarSkeleton /> : <WeeklyCalendar auctions={weeklyAuctions} />}
            </section>

            {/* ── Sección 2: KPIs Mensuales ── */}
            <section style={{ marginBottom: 40 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#021344' }}>
                    Resumen mensual
                  </h2>
                  <p style={{ margin: '3px 0 0', fontSize: '0.8125rem', color: '#6B7A99' }}>
                    Todos los clientes combinados
                  </p>
                </div>
                <input
                  type="month"
                  value={selectedMonth}
                  max={currentMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                  style={{
                    marginLeft: 'auto',
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1px solid #E2E5EC',
                    background: '#fff',
                    color: '#021344',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    outline: 'none',
                  }}
                />
              </div>
              {loading ? <SummarySkeleton /> : <SummaryCards summary={summary} period="month" />}
            </section>

            {/* ── Sección 3: Estadísticas por Cliente ── */}
            <section>
              <SectionHeader
                title="Estadísticas por cliente"
                subtitle={`${format(new Date(`${selectedMonth}-01`), 'MMMM yyyy', { locale: es })} · Clic en encabezados para ordenar`}
              />
              {loading ? <TableSkeleton /> : <TenantStatsTable stats={tenantStats} period="month" />}
            </section>

            {/* ── Sección 4: KPIs Anuales ── */}
            <section style={{ margin: '48px 0 40px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#021344' }}>
                    Resumen anual
                  </h2>
                  <p style={{ margin: '3px 0 0', fontSize: '0.8125rem', color: '#6B7A99' }}>
                    Todos los clientes combinados · Incluye clientes nuevos del año
                  </p>
                </div>
                <select
                  value={selectedYear}
                  onChange={e => setSelectedYear(parseInt(e.target.value, 10))}
                  style={{
                    marginLeft: 'auto',
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1px solid #E2E5EC',
                    background: '#fff',
                    color: '#021344',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    outline: 'none',
                  }}
                >
                  {(availableYears.length ? availableYears : [selectedYear]).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              {yearlyLoading ? <SummarySkeleton /> : <SummaryCards summary={yearlySummary} period="year" />}
            </section>

            {/* ── Sección 5: Estadísticas por Cliente (anual) ── */}
            <section>
              <SectionHeader
                title="Estadísticas por cliente — anual"
                subtitle={`Año ${selectedYear} · Clic en encabezados para ordenar`}
              />
              {yearlyLoading ? <TableSkeleton /> : <TenantStatsTable stats={yearlyTenantStats} period="year" />}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

/* ── Shared section header ── */
function SectionHeader({ title, subtitle, loading }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#021344' }}>
          {title}
        </h2>
        {subtitle && (
          <p style={{ margin: '3px 0 0', fontSize: '0.8125rem', color: '#6B7A99' }}>
            {subtitle}
          </p>
        )}
      </div>
      {loading && (
        <span style={{ fontSize: '0.8125rem', color: '#9AA3B5', animation: 'pulse 1.5s infinite' }}>
          Cargando…
        </span>
      )}
    </div>
  );
}

/* ── Skeleton loaders ── */
function ThreeWeekSkeleton() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} style={{ height: 400, borderRadius: 12, background: '#E2E5EC', animation: 'pulse 1.5s infinite' }} />
      ))}
    </div>
  );
}

function CalendarSkeleton() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} style={{ height: 120, borderRadius: 10, background: '#E2E5EC', animation: 'pulse 1.5s infinite' }} />
      ))}
    </div>
  );
}

function SummarySkeleton() {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} style={{ flex: '1 1 160px', height: 90, borderRadius: 10, background: '#E2E5EC', animation: 'pulse 1.5s infinite' }} />
      ))}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div style={{ borderRadius: 10, border: '1px solid #E2E5EC', overflow: 'hidden' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{ height: 44, background: i % 2 === 0 ? '#fff' : '#F8F9FC', borderBottom: '1px solid #F0F2F8', animation: 'pulse 1.5s infinite' }} />
      ))}
    </div>
  );
}

Dashboard.propTypes = {
  onLogout: PropTypes.func.isRequired,
};

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  loading: PropTypes.bool,
};

export default Dashboard;
